import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';

// Base API URL
const API_BASE_URL = 'https://apissc.sainikschoolcadet.com/api/students';

// Define color scheme based on 60-30-10 rule
const COLORS = {
  primary: '#8D43F8', // 60% - Main purple (dominant)
  secondary: '#4423CC', // 30% - Darker purple (secondary)
  accent: '#FF6B6B', // 10% - Accent color (red for contrast)
  background: '#F9F7FF', // Light purple background
  cardBg: '#FFFFFF', // Card background
  text: {
    dark: '#2D2D2D',
    medium: '#4A4A68',
    light: '#8A8AA3',
  },
  success: '#4CAF50',
  warning: '#FFC107',
  chart: {
    purple1: '#8D43F8',
    purple2: '#4423CC',
    purple3: '#B57FFF',
    purpleGradient: ['#8D43F8', '#4423CC'],
  }
};

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [enrollmentTrends, setEnrollmentTrends] = useState({ monthlyEnrollment: [], cumulativeEnrollment: [] });
  const [geoDistribution, setGeoDistribution] = useState({ byState: [] });
  const [schoolDistribution, setSchoolDistribution] = useState({ bySchool: [], byCourse: [], bySchoolAndCourse: [] });
  const [dailyRegistrations, setDailyRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [enrollmentFilter, setEnrollmentFilter] = useState('all');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  
  // Registration chart states
  const [allRegistrationsData, setAllRegistrationsData] = useState([]);
  const [formattedDailyRegistrations, setFormattedDailyRegistrations] = useState([]);
  const [registrationPeriod, setRegistrationPeriod] = useState('all');
  
  // Add ref for dropdown
  const filterDropdownRef = useRef(null);
  
  // Effect to close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        try {
          const dashboardResponse = await axios.get(`${API_BASE_URL}/analytics/dashboard`);
          setDashboardData(dashboardResponse.data);
        } catch (err) {
          console.error('Error fetching dashboard data:', err);
          throw new Error('Failed to load dashboard data');
        }
        
        try {
          const trendsResponse = await axios.get(`${API_BASE_URL}/analytics/enrollment-trends`);
          setEnrollmentTrends(trendsResponse.data);
        } catch (err) {
          console.error('Error fetching enrollment trends:', err);
          throw new Error('Failed to load enrollment trends');
        }
        
        try {
          const geoResponse = await axios.get(`${API_BASE_URL}/analytics/geographic-distribution`);
          setGeoDistribution(geoResponse.data);
        } catch (err) {
          console.error('Error fetching geographic distribution:', err);
          throw new Error('Failed to load geographic data');
        }
        
        try {
          const schoolResponse = await axios.get(`${API_BASE_URL}/analytics/school-course-distribution`);
          setSchoolDistribution(schoolResponse.data);
        } catch (err) {
          console.error('Error fetching school distribution:', err);
          throw new Error('Failed to load school distribution data');
        }
        
        // Fetch daily registrations
        try {
          const dailyRegistrationsResponse = await axios.get(`${API_BASE_URL}/daily-registrations`);
          if (dailyRegistrationsResponse.data.success && dailyRegistrationsResponse.data.dailyRegistrations) {
            const registrationsData = dailyRegistrationsResponse.data.dailyRegistrations;
            setDailyRegistrations(registrationsData);
            
            // Format data for registration chart and initialize both state variables
            const formattedData = registrationsData.map(item => ({
              date: formatDate(item.date),
              value: parseInt(item.count, 10) || 0
            }));
            
            setAllRegistrationsData(formattedData);
            setFormattedDailyRegistrations(formattedData);
          } else {
            throw new Error('Invalid daily registrations data format');
          }
        } catch (err) {
          console.error('Error fetching daily registrations:', err);
          setError('Failed to load daily registrations data');
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error in data fetching:', error);
        setError(error.message || 'Failed to load dashboard data. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, []);

  // Filter function for registration chart time periods
  const filterRegistrationsByMonths = (months) => {
    if (months === 'all') {
      setFormattedDailyRegistrations(allRegistrationsData);
      return;
    }
    
    const monthsNum = parseInt(months);
    const cutoffDate = new Date();
    cutoffDate.setMonth(cutoffDate.getMonth() - monthsNum);
    
    const filteredData = allRegistrationsData.filter(item => {
      const itemDate = new Date(item.date);
      return itemDate >= cutoffDate;
    });
    
    setFormattedDailyRegistrations(filteredData);
  };

  // Format month string for display (2025-03 to Mar 2025)
  const formatMonth = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleString('default', { month: 'short' }) + ' ' + year;
  };
  
  // Format date string for display (2022-05-04 to May 4, 2022)
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('default', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Format enrollment data for charts
  const formattedMonthlyEnrollment = enrollmentTrends?.monthlyEnrollment?.map(item => ({
    name: formatMonth(item.month),
    value: parseInt(item.count, 10)
  })) || [];

  const formattedCumulativeEnrollment = enrollmentTrends?.cumulativeEnrollment?.map(item => ({
    name: formatMonth(item.month),
    value: item.cumulative
  })) || [];
  
  // Format state-wise enrollment data for bar chart
  const stateEnrollmentData = geoDistribution?.byState?.map(item => ({
    state: item.state,
    students: parseInt(item.count, 10)
  })).sort((a, b) => b.students - a.students) || [];
  
  // Convert school counts to numbers for charts
  const schoolData = dashboardData?.topSchools?.map(item => ({
    ...item,
    count: parseInt(item.count, 10)
  })) || [];
  
  // Convert course counts to numbers for charts
  const courseData = schoolDistribution?.byCourse?.map(item => ({
    ...item,
    count: parseInt(item.count, 10)
  })) || [];
  
  // Custom Card Component
  const Card = ({ children, className = '' }) => (
    <div className={`bg-white rounded-lg shadow-md overflow-hidden ${className}`}>
      {children}
    </div>
  );

  // Custom Card Header Component
  const CardHeader = ({ title, children }) => (
    <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
      <h3 className="font-semibold text-lg text-gray-800">{title}</h3>
      {children}
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen" style={{ backgroundColor: COLORS.background }}>
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4" style={{ borderColor: COLORS.primary }}></div>
          <p className="mt-4 font-medium" style={{ color: COLORS.primary }}>Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen" style={{ backgroundColor: COLORS.background }}>
        <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-lg shadow-md max-w-lg">
          <div className="flex items-center">
            <svg className="h-6 w-6 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="font-bold">Error Loading Dashboard</p>
          </div>
          <p className="mt-2">{error}</p>
          <button 
            className="mt-4 text-white py-2 px-4 rounded transition-colors duration-200"
            style={{ backgroundColor: COLORS.primary, hover: COLORS.secondary }}
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: COLORS.background }}>
      {/* Dashboard Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold" style={{ color: COLORS.text.dark }}>CRM Dashboard</h1>
        <p style={{ color: COLORS.text.light }}>Welcome to your student management overview</p>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {/* Total Students */}
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg" style={{ backgroundColor: `${COLORS.primary}20` }}>
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: COLORS.primary }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
            </div>
            <h2 className="text-3xl font-bold mb-1" style={{ color: COLORS.text.dark }}>{dashboardData?.totalStudents.toLocaleString() || 0}</h2>
            <p style={{ color: COLORS.text.light }}>Total Students</p>
          </div>
        </Card>
        
        {/* Male Students */}
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg" style={{ backgroundColor: `${COLORS.secondary}20` }}>
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: COLORS.secondary }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div className="px-2 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: `${COLORS.secondary}20`, color: COLORS.secondary }}>
                {dashboardData?.genderDistribution?.malePercentage || 0}%
              </div>
            </div>
            <h2 className="text-3xl font-bold mb-1" style={{ color: COLORS.text.dark }}>{dashboardData?.genderDistribution?.male.toLocaleString() || 0}</h2>
            <p style={{ color: COLORS.text.light }}>Male Students</p>
          </div>
        </Card>
        
        {/* Female Students */}
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg" style={{ backgroundColor: `${COLORS.accent}20` }}>
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: COLORS.accent }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div className="px-2 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: `${COLORS.accent}20`, color: COLORS.accent }}>
                {dashboardData?.genderDistribution?.femalePercentage || 0}%
              </div>
            </div>
            <h2 className="text-3xl font-bold mb-1" style={{ color: COLORS.text.dark }}>{dashboardData?.genderDistribution?.female.toLocaleString() || 0}</h2>
            <p style={{ color: COLORS.text.light }}>Female Students</p>
          </div>
        </Card>
        
        {/* New Enrollments */}
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg" style={{ backgroundColor: `${COLORS.success}20` }}>
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: COLORS.success }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="px-2 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: `${COLORS.success}20`, color: COLORS.success }}>
                New
              </div>
            </div>
            <h2 className="text-3xl font-bold mb-1" style={{ color: COLORS.text.dark }}>
              {(dashboardData?.recentEnrollmentTrend?.length > 0 
                ? parseInt(dashboardData.recentEnrollmentTrend[dashboardData.recentEnrollmentTrend.length - 1].count, 10)
                : 0).toLocaleString()}
            </h2>
            <p style={{ color: COLORS.text.light }}>Users Registered this month</p>
          </div>
        </Card>
      </div>
      
      {/* Enrollment Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Monthly Enrollment with Filter Dropdown */}
        <Card>
          <CardHeader title="Monthly Enrollment">
            <div className="relative inline-block" ref={filterDropdownRef}>
              <button
                type="button"
                className="inline-flex items-center justify-between w-40 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsFilterOpen(!isFilterOpen);
                }}
              >
                <span>{enrollmentFilter === 'all' ? 'All Time' : 'Last 12 Months'}</span>
                <svg className="w-5 h-5 ml-2 -mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
              
              {isFilterOpen && (
                <div className="absolute right-0 z-50 w-40 mt-1 origin-top-right bg-white border border-gray-200 divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setEnrollmentFilter('all');
                        setIsFilterOpen(false);
                      }}
                      className={`block w-full px-4 py-2 text-sm text-left ${
                        enrollmentFilter === 'all' ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      All Time
                    </button>
                    <button
                      onClick={() => {
                        setEnrollmentFilter('12months');
                        setIsFilterOpen(false);
                      }}
                      className={`block w-full px-4 py-2 text-sm text-left ${
                        enrollmentFilter === '12months' ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Last 12 Months
                    </button>
                  </div>
                </div>
              )}
            </div>
          </CardHeader>
          <div className="p-4">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={enrollmentFilter === 'all' ? formattedMonthlyEnrollment : formattedMonthlyEnrollment.slice(-12)}
                  margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
                >
                  <CartesianGrid vertical={false} stroke="#f5f5f5" />
                  <XAxis 
                    dataKey="name" 
                    axisLine={false} 
                    tickLine={false}
                    tick={{ fill: COLORS.text.light, fontSize: 12 }}
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false}
                    tick={{ fill: COLORS.text.light, fontSize: 12 }}
                  />
                  <Tooltip 
                    contentStyle={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', border: 'none' }}
                    labelStyle={{ color: COLORS.text.dark }}
                    itemStyle={{ color: COLORS.primary }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke={COLORS.primary} 
                    strokeWidth={3}
                    dot={{ stroke: COLORS.primary, strokeWidth: 2, fill: 'white', r: 4 }}
                    activeDot={{ stroke: COLORS.primary, strokeWidth: 2, r: 6, fill: 'white' }}
                    name="New Students"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Card>
        
        {/* Cumulative Enrollment */}
        <Card>
          <CardHeader title="Growth Overtime" />
          <div className="p-4">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={formattedCumulativeEnrollment}
                  margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
                >
                  <defs>
                    <linearGradient id="colorCumulative" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS.secondary} stopOpacity={0.8}/>
                      <stop offset="95%" stopColor={COLORS.secondary} stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} stroke="#f5f5f5" />
                  <XAxis 
                    dataKey="name" 
                    axisLine={false} 
                    tickLine={false}
                    tick={{ fill: COLORS.text.light, fontSize: 12 }}
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false}
                    tick={{ fill: COLORS.text.light, fontSize: 12 }}
                  />
                  <Tooltip 
                    contentStyle={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', border: 'none' }}
                    labelStyle={{ color: COLORS.text.dark }}
                    itemStyle={{ color: COLORS.secondary }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="value" 
                    stroke={COLORS.secondary} 
                    fillOpacity={1}
                    fill="url(#colorCumulative)"
                    name="Total Students"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Card>
      </div>
      
      {/* Distribution Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Daily Registrations Chart with filter */}
        <Card>
          <CardHeader title="Daily Registrations">
            <div className="ml-auto">
              <select 
                className="px-3 py-1 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                onChange={(e) => {
                  const selectedValue = e.target.value;
                  setRegistrationPeriod(selectedValue);
                  filterRegistrationsByMonths(selectedValue);
                }}
                value={registrationPeriod}
              >
                <option value="all">All Time</option>
                <option value="1">Last Month</option>
                <option value="3">Last 3 Months</option>
                <option value="6">Last 6 Months</option>
                <option value="12">Last 12 Months</option>
              </select>
            </div>
          </CardHeader>
          <div className="p-4">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={formattedDailyRegistrations}
                  margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
                >
                  <CartesianGrid vertical={false} stroke="#f5f5f5" />
                  <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    tick={false}
                    height={20}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: COLORS.text.light, fontSize: 12 }}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', border: 'none' }}
                    labelStyle={{ color: COLORS.text.dark }}
                    itemStyle={{ color: COLORS.primary }}
                    formatter={(value) => [`${value} registrations`, 'Count']}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke={COLORS.primary}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 6, fill: COLORS.primary }}
                    name="Registrations"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Card>

        {/* Course Distribution */}
        <Card>
          <CardHeader title="Class Distribution" />
          <div className="p-4">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  layout="vertical"
                  data={courseData}
                  margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
                >
                  <CartesianGrid horizontal={false} stroke="#f5f5f5" />
                  <XAxis
                    type="number"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: COLORS.text.light, fontSize: 12 }}
                  />
                  <YAxis
                    dataKey="c_entry"
                    type="category"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: COLORS.text.light, fontSize: 12 }}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', border: 'none' }}
                    labelStyle={{ color: COLORS.text.dark }}
                    itemStyle={{ color: COLORS.primary }}
                  />
                  <Bar
                    dataKey="count"
                    fill={COLORS.primary}
                    radius={[0, 4, 4, 0]}
                    name="Students"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Card>
      </div>

      {/* State-wise enrollment */}
      <Card className="mb-6">
        <CardHeader title="State-wise Enrollment" />
        <div className="p-4">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={stateEnrollmentData}
                margin={{ top: 5, right: 10, left: 50, bottom: 60 }}
              >
                <CartesianGrid vertical={false} stroke="#f5f5f5" />
                <XAxis
                  dataKey="state"
                  type="category"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: COLORS.text.light, fontSize: 12 }}
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis
                  type="number"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: COLORS.text.light, fontSize: 12 }}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', border: 'none' }}
                  labelStyle={{ color: COLORS.text.dark }}
                  itemStyle={{ color: COLORS.secondary }}
                />
                <Bar
                  dataKey="students"
                  fill={COLORS.secondary}
                  radius={[4, 4, 0, 0]}
                  name="Students"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Card>

      {/* Top Schools */}
<Card className="mb-6">
  <CardHeader title="Top Schools" />
  <div className="p-6">
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {schoolData.map((school, index) => (
        <div key={index} className="flex items-start p-4 rounded-lg" style={{ backgroundColor: `${COLORS.primary}10` }}>
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 mr-4 font-medium ${
            index === 0 ? 'bg-yellow-100 text-yellow-600' : 
            index === 1 ? 'bg-gray-200 text-gray-600' : 
            'bg-amber-100 text-amber-600'
          }`}>
            {index + 1}
          </div>
          <div className="flex-1">
            <div className="mb-2">
              <p className="font-medium" style={{ color: COLORS.text.dark }}>{school.c_school || "Not specified"}</p>
              <p className="text-xs" style={{ color: COLORS.text.light }}>{school.count} Students</p>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div 
                className="h-1.5 rounded-full transition-all duration-500 ease-out"
                style={{ 
                  width: `${(school.count / dashboardData.totalStudents * 100).toFixed(0)}%`,
                  backgroundColor: index === 0 ? COLORS.primary : 
                                index === 1 ? COLORS.secondary : 
                                COLORS.accent
                }}
              ></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
</Card>
</div>
  );
};

export default Dashboard;